import { z } from "zod";

/**
 * The clarifying question (openspec change `agent-turn`, spec `agent-questions`, D6–D8). The
 * agent calls the internal `ask_user` tool with one question and 2 to 4 choices; the turn ends
 * after that step. The user's pick is a normal user message whose text is the choice, and the
 * server binds it to the question with a `data-question-answer` part.
 */
export const ASK_USER_TOOL_NAME = "ask_user";

/** The stored tool part type of a question (AI SDK `tool-<name>`). */
export const ASK_USER_PART = `tool-${ASK_USER_TOOL_NAME}` as const;

export const QUESTION_LIMITS = {
  question: 300,
  choice: 80,
  context: 200,
  minChoices: 2,
  maxChoices: 4,
} as const;

/** What the model passes to `ask_user` (D6). */
export const askUserInputSchema = z.object({
  question: z.string().trim().min(1).max(QUESTION_LIMITS.question),
  choices: z
    .array(z.string().trim().min(1).max(QUESTION_LIMITS.choice))
    .min(QUESTION_LIMITS.minChoices)
    .max(QUESTION_LIMITS.maxChoices)
    .refine((choices) => new Set(choices.map((c) => c.toLowerCase())).size === choices.length, {
      message: "choices must be distinct",
    }),
  /** Whether the user may answer in their own words (the card's "Answer in your own words"). */
  allowOther: z.boolean().default(true),
  /** One line on why the question is asked. */
  context: z.string().trim().max(QUESTION_LIMITS.context).optional(),
});
export type AskUserInput = z.infer<typeof askUserInputSchema>;

/**
 * What `ask_user` returns to the model: the question is shown and the turn ends, or it was not
 * (a second question, an invalid one) with a reason the model reads, also in later turns.
 */
export const askUserOutputSchema = z.union([
  z.object({ asked: z.literal(true) }),
  z.object({ asked: z.literal(false), error: z.string() }),
]);
export type AskUserOutput = z.infer<typeof askUserOutputSchema>;

/** The reason a second question in the same turn fails (readable by the model). */
export const ALREADY_ASKED = "already_asked";

/** The user message part that binds an answer to its question (D7), written by the server. */
export const QUESTION_ANSWER_PART = "data-question-answer";

export const questionAnswerDataSchema = z.object({
  toolCallId: z.string().min(1).max(200),
  /** The chosen choice's index in the question's choices; null for an answer in own words. */
  choiceIndex: z
    .int()
    .min(0)
    .max(QUESTION_LIMITS.maxChoices - 1)
    .nullable(),
});
export type QuestionAnswerData = z.infer<typeof questionAnswerDataSchema>;

/**
 * The client's reference to the question a message answers (`sendMessageRequestSchema`): only a
 * tool call ID. The server checks it belongs to a question of the same conversation and works
 * out the choice itself; any other reference is ignored.
 */
export const questionAnswerRefSchema = z.object({
  toolCallId: z.string().min(1).max(200),
});
export type QuestionAnswerRef = z.infer<typeof questionAnswerRefSchema>;

/** A stored question tool part, when it is one that was asked (its input parsed). */
export function askedQuestionOf(part: unknown): { toolCallId: string; input: AskUserInput } | null {
  const p = part as { type?: unknown; toolCallId?: unknown; state?: unknown; input?: unknown };
  if (
    p === null ||
    typeof p !== "object" ||
    p.type !== ASK_USER_PART ||
    typeof p.toolCallId !== "string" ||
    p.state !== "output-available" ||
    (p as { output?: { asked?: unknown } }).output?.asked !== true
  ) {
    return null;
  }
  const input = askUserInputSchema.safeParse(p.input);
  return input.success ? { toolCallId: p.toolCallId, input: input.data } : null;
}

/** The answer part of a message, if it has a well-formed one. */
export function questionAnswerOf(parts: readonly unknown[]): QuestionAnswerData | null {
  for (const part of parts) {
    const p = part as { type?: unknown; data?: unknown } | null;
    if (p?.type === QUESTION_ANSWER_PART) {
      const data = questionAnswerDataSchema.safeParse(p.data);
      if (data.success) {
        return data.data;
      }
    }
  }
  return null;
}

/**
 * The index of the choice `text` picks, compared trimmed and case-insensitively; null when it
 * matches none (an answer in the user's own words).
 */
export function choiceIndexOf(choices: readonly string[], text: string): number | null {
  const wanted = text.trim().toLowerCase();
  const index = choices.findIndex((c) => c.trim().toLowerCase() === wanted);
  return index === -1 ? null : index;
}
